// AdminAiAssistantPage.jsx — chat with the VoiceFabric AI assistant.
//
// MVP scope:
//  • Uses `llm_chat` resolver on the backend → Anthropic-first, Emergent fallback.
//  • Drafts VoiceFabric rules on request and surfaces them in a Save-Ready card.
//  • Wires ONE live tool (GymSales phone lookup); every other requested
//    integration (Onebill / ConnectWise / CRM) logs to the wishlist.
//  • Session-scoped history — refresh clears the tab, no server-side persistence.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, Send, Loader2, User as UserIcon, Copy, CheckCircle2, Sparkles, MessageSquarePlus, Wrench, Save,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  'Draft a rule that fires when an outbound call mentions "not interested" and lasts under 45 seconds.',
  'Show me what a VoiceFabric rule that escalates every call scored below 0.6 to the domain admin looks like.',
  'Which packages can I auto-connect right now, and which are Coming Soon?',
  'Explain the difference between a "when.any_phrase" match and a "when.min_duration_seconds" filter.',
];

const RuleDraftCard = ({ rule, domainId, domainName, token, onSaved }) => {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);   // { id, name } on success
  const [saveErr, setSaveErr] = useState('');
  const json = JSON.stringify(rule, null, 2);
  const copy = () => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };
  const save = async () => {
    if (!domainId) {
      toast.error('Pick a domain at the top of the page first.');
      return;
    }
    setSaving(true); setSaveErr('');
    try {
      const r = await axios.post(
        `${API}/admin/domains/${domainId}/voicefabric/rules`,
        rule,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSaved({ id: r.data.id, name: r.data.name });
      toast.success(`Rule "${r.data.name}" created on ${domainName || 'domain'}.`);
      onSaved?.(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      setSaveErr(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error(typeof msg === 'string' ? msg : 'Save failed — see details on the card.');
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg overflow-hidden" data-testid="rule-draft-card">
      <div className="px-3 py-2 flex items-center gap-2 bg-emerald-500/15 flex-wrap">
        <Sparkles className="w-4 h-4 text-emerald-300" />
        <div className="text-emerald-100 text-xs font-semibold flex-1 min-w-0 truncate">
          Draft rule: <span className="font-mono">{rule.name}</span>
        </div>
        {!saved && (
          <button
            type="button"
            onClick={save}
            disabled={saving || !domainId}
            title={!domainId ? 'Pick a domain at the top of the page first' : `Create this rule on ${domainName || 'the selected domain'}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-white bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded px-2 py-1"
            data-testid="save-rule-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {saving ? 'Saving…' : 'Save to VoiceFabric'}
          </button>
        )}
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-100 hover:text-white bg-white/10 rounded px-2 py-0.5"
          data-testid="copy-rule-btn"
        >
          {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy JSON'}
        </button>
      </div>
      <pre className="text-[10.5px] text-emerald-50/90 font-mono p-3 whitespace-pre-wrap max-h-[200px] overflow-auto">{json}</pre>
      {saved && (
        <div className="px-3 py-2 bg-emerald-500/20 border-t border-emerald-500/30 text-[11px] text-emerald-100 flex items-center gap-2" data-testid="rule-saved-badge">
          <CheckCircle2 className="w-3.5 h-3.5" />
          <div className="flex-1">
            Live now on <b>{domainName}</b> — <span className="font-mono opacity-70">{saved.id.slice(0, 8)}</span>
          </div>
          <a
            href={`/admin/domains/${domainId}`}
            className="text-emerald-200 hover:text-white underline text-[10px] uppercase tracking-wider font-semibold"
          >
            Open in Admin
          </a>
        </div>
      )}
      {saveErr && !saved && (
        <div className="px-3 py-2 bg-rose-500/15 border-t border-rose-500/30 text-[11px] text-rose-100" data-testid="rule-save-error">
          <b>Couldn&apos;t save:</b> {saveErr}
        </div>
      )}
      {!saved && !saveErr && (
        <div className="px-3 py-2 text-[10px] text-emerald-100/70 border-t border-emerald-500/20">
          Click <b className="text-emerald-100">Save to VoiceFabric</b> to create this rule on {domainName ? <b className="text-emerald-100">{domainName}</b> : 'the selected domain'}. You can also copy the JSON to paste it in manually.
        </div>
      )}
    </div>
  );
};

const AdminAiAssistantPage = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // ── Domain picker — every "Save to VoiceFabric" click writes to
  // whichever tenant is selected here. Persists across chat turns.
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const list = (r.data?.domains || r.data || [])
          .filter((d) => d.status !== 'inactive')
          .map((d) => ({
            id: d.id,
            name: (d.company?.name) || d.yabbit_domain || d.slug || d.id,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setDomains(list);
        if (list.length && !selectedDomain) setSelectedDomain(list[0].id);
      } catch (_e) {
        /* handled by disabled save button */
      }
    })();
    return () => { cancelled = true; };
  }, [token, selectedDomain]);
  const selectedDomainName = domains.find((d) => d.id === selectedDomain)?.name;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const send = useCallback(async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/ai-assistant/chat`, {
        messages: nextMessages,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages([
        ...nextMessages,
        {
          role: 'assistant',
          content: r.data.reply,
          provider: r.data.provider_used,
          rule_draft: r.data.rule_draft,
        },
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assistant failed to respond');
      setMessages(nextMessages);
    } finally { setSending(false); }
  }, [input, sending, messages, token]);

  const reset = () => setMessages([]);

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-4 flex flex-col h-[calc(100vh-3rem)]" data-testid="ai-assistant-page">
        <div className="flex items-start justify-between flex-wrap gap-3 shrink-0">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Bot className="w-6 h-6 text-teal-400" />
              AI Assistant
            </h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              Design VoiceFabric rules, ask about integrations, or look up GymSales leads.
              Powered by Claude Sonnet (your direct API key), with the Emergent Universal Key as fallback.
            </p>
          </div>
          <button
            type="button"
            onClick={reset}
            disabled={messages.length === 0 || sending}
            className="inline-flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white rounded px-3 py-1.5 border border-white/10"
            data-testid="ai-reset-btn"
          >
            <MessageSquarePlus className="w-3.5 h-3.5" /> New conversation
          </button>
        </div>

        {/* Domain picker — every rule saved from this page lands on the tenant selected here */}
        <div className="shrink-0 flex items-center gap-2 flex-wrap bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2">
          <span className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Save rules to</span>
          <select
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-teal-400"
            data-testid="ai-domain-picker"
          >
            {domains.length === 0 && <option value="">Loading tenants…</option>}
            {domains.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <span className="text-[10px] text-white/40">
            Change any time — every <b className="text-white/70">Save to VoiceFabric</b> button targets this tenant.
          </span>
        </div>

        {/* Chat area */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto space-y-4 bg-white/[0.02] border border-white/10 rounded-xl p-4"
          data-testid="ai-chat-area"
        >
          {messages.length === 0 ? (
            <div className="text-center text-white/40 text-sm py-8">
              <Bot className="w-10 h-10 mx-auto text-white/30 mb-3" />
              <p className="mb-4">Ask me anything about VoiceFabric rules or integrations.</p>
              <div className="max-w-lg mx-auto space-y-2 text-left">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    className="block w-full text-left text-xs text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2"
                    data-testid={`ai-suggestion-${s.slice(0, 20).replace(/\s+/g, '-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}
                data-testid={`ai-msg-${m.role}-${i}`}
              >
                <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${m.role === 'user' ? 'bg-teal-500 text-white' : 'bg-white/10 text-white/80'}`}>
                  {m.role === 'user' ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`min-w-0 max-w-[85%] ${m.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block text-left rounded-lg px-3.5 py-2 text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-teal-500 text-white'
                        : 'bg-white/5 border border-white/10 text-white/90'
                    }`}
                  >
                    {/* Strip the rule JSON code fence from the assistant reply when we have a structured rule_draft — the RuleDraftCard renders it prettier below */}
                    {m.role === 'assistant' && m.rule_draft
                      ? m.content.replace(/```json[\s\S]*?```/g, '').trim()
                      : m.content}
                  </div>
                  {m.rule_draft && (
                    <RuleDraftCard
                      rule={m.rule_draft}
                      domainId={selectedDomain}
                      domainName={selectedDomainName}
                      token={token}
                    />
                  )}
                  {m.role === 'assistant' && m.provider && (
                    <p className="text-[9px] text-white/30 mt-1 uppercase tracking-widest font-semibold">
                      via {m.provider.replace('_', ' → ')}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-white/60" />
              </div>
              <div className="text-white/50 text-sm italic self-center">Thinking…</div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="shrink-0 flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Ask about rules, look up a lead, request an integration…"
            rows={2}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm focus:outline-none focus:border-teal-400 resize-none"
            data-testid="ai-input"
          />
          <button
            type="button"
            onClick={() => send()}
            disabled={!input.trim() || sending}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white font-semibold rounded-lg text-sm"
            data-testid="ai-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send
          </button>
        </div>

        <div className="shrink-0 text-[10px] text-white/40 flex items-center gap-1.5">
          <Wrench className="w-3 h-3" />
          Live tools: GymSales lookup · Wishlist logger. Onebill / ConnectWise / generic CRM — Coming Soon.
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAiAssistantPage;
